export type FernieDictationNotice =
	| "failed"
	| "limit"
	| "noMicrophone"
	| "permissionDenied"
	| "silence"
	| "unavailable";

export interface SpeechRecognitionResultLike {
	readonly [index: number]: { readonly transcript: string };
}

interface SpeechRecognitionResultEventLike {
	resultIndex: number;
	results: ArrayLike<SpeechRecognitionResultLike>;
}

interface SpeechRecognitionErrorEventLike {
	error: string;
}

export interface SpeechRecognitionLike {
	continuous: boolean;
	lang: string;
	onend: (() => void) | null;
	onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
	onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
	abort: () => void;
	start: () => void;
	stop: () => void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

interface SpeechRecognitionHost {
	SpeechRecognition?: SpeechRecognitionConstructor;
	webkitSpeechRecognition?: SpeechRecognitionConstructor;
}

const MAX_CAPTURE_MS = 60_000;

// Safari can leave a stopped recognition without ever firing its end event.
const TRANSCRIBE_TIMEOUT_MS = 10_000;

const resolveSpeechRecognition = (): SpeechRecognitionConstructor | null => {
	const host = globalThis as typeof globalThis & SpeechRecognitionHost;
	return host.SpeechRecognition ?? host.webkitSpeechRecognition ?? null;
};

const appendDictatedText = (draft: string, dictated: string): string => {
	const text = dictated.trim();
	if (!text) return draft;
	if (!draft || /\s$/.test(draft)) return draft + text;
	return `${draft} ${text}`;
};

const isDictationEnabled = (aiChatConfig: string): boolean => {
	try {
		const parsed: unknown = JSON.parse(aiChatConfig);
		return typeof parsed === "object" && parsed !== null && "dictation" in parsed && parsed.dictation === true;
	} catch {
		return false;
	}
};

const mapRecognitionErrorToNotice = (error: string): FernieDictationNotice => {
	switch (error) {
		case "not-allowed":
			return "permissionDenied";
		case "audio-capture":
			return "noMicrophone";
		case "no-speech":
			return "silence";
		case "service-not-allowed":
		case "language-not-supported":
			return "unavailable";
		default:
			return "failed";
	}
};

const fernieDictation = {
	appendDictatedText,
	isDictationEnabled,
	mapRecognitionErrorToNotice,
	MAX_CAPTURE_MS,
	resolveSpeechRecognition,
	TRANSCRIBE_TIMEOUT_MS
};

export default fernieDictation;
